// Back-in-stock "Notify me" (rendered by snippets/main-product__notify-me.liquid).
// The buy-buttons area renders two swappable buttons: the normal (disabled when
// sold out) submit button and a "Notify when available" opener. This controller
// toggles the pair on PUB_SUB_EVENTS.variantChange and opens the notify modal —
// a theme <modal-dialog> (global.js: Esc/overlay close, focus trap, scroll lock;
// it moves itself to <body>, hence the lookups by id instead of containment).
// Submitting posts to the Gobi Loyalty app proxy and fires Shopify's native
// customer form in the background so marketing consent is captured through the
// store's own opt-in / double-opt-in flow (the restock email is sent by Shopify
// Email via Flow, which only delivers to subscribed customers).

if (!customElements.get("notify-me-form")) {
  customElements.define(
    "notify-me-form",
    class NotifyMeForm extends HTMLElement {
      static EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

      connectedCallback() {
        const sectionId = this.dataset.sectionId;
        // The <modal-dialog> re-parents itself to <body> on upgrade.
        this.modal = document.getElementById(`NotifyMeModal-${sectionId}`);
        this.form = this.modal?.querySelector("form.product-notify-me__form");
        if (!this.modal || !this.form) return;

        this.emailInput = this.form.elements.email;
        this.variantInput = this.form.elements.variant_id;
        this.statusEl = this.modal.querySelector(".product-notify-me__status");
        this.submitButton = this.modal.querySelector(".product-notify-me__submit");

        this.form.addEventListener("submit", this.onSubmit.bind(this));

        // "Notify when available" trigger(s) + the add-to-cart button they replace.
        this.openButtons = Array.from(
          document.querySelectorAll(`[data-notify-me-open][data-section-id="${sectionId}"]`)
        );
        this.cartButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
        this.openButtons.forEach((button) =>
          button.addEventListener("click", this.openModal.bind(this))
        );

        if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange.bind(this)
          );
        }
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      openModal(event) {
        this.modal.show(event.currentTarget);
        this.emailInput?.focus();
      }

      onVariantChange(event) {
        if (event?.data?.sectionId !== this.dataset.sectionId) return;
        const variant = event.data.variant;
        const soldOut = Boolean(variant) && !variant.available;

        this.openButtons.forEach((button) => button.classList.toggle("hidden", !soldOut));
        this.cartButton?.classList.toggle("hidden", soldOut);

        if (soldOut && this.variantInput && String(this.variantInput.value) !== String(variant.id)) {
          this.variantInput.value = variant.id;
          this.resetAfterVariantSwitch();
        }
      }

      resetAfterVariantSwitch() {
        this.hideStatus();
        this.form.classList.remove("product-notify-me__form--done");
        this.submitButton?.removeAttribute("disabled");
      }

      // Optimistic: the thanks message appears instantly (no loading state) and
      // the request completes in the background. Only a definitive failure flips
      // the modal back to the form with an error, so the shopper can retry.
      async onSubmit(event) {
        event.preventDefault();
        const email = (this.emailInput?.value || "").trim();
        if (!NotifyMeForm.EMAIL_RE.test(email)) {
          this.showStatus(this.dataset.textInvalidEmail, true);
          this.emailInput?.focus();
          return;
        }

        const body = new FormData(this.form);
        this.showStatus(this.dataset.textSuccess, false);
        this.form.classList.add("product-notify-me__form--done");
        this.subscribeMarketing(email);

        try {
          const response = await fetch("/apps/loyalty/notify", {
            method: "POST",
            body,
            headers: { Accept: "application/json" },
          });
          const result = await response.json().catch(() => null);

          if (response.ok && result?.ok) {
            if (result.alreadyAvailable) {
              this.showStatus(this.dataset.textAlreadyAvailable, false);
            }
          } else if (result?.error === "invalid_email") {
            this.restoreForm(this.dataset.textInvalidEmail);
          } else {
            this.restoreForm(this.dataset.textError);
          }
        } catch (e) {
          this.restoreForm(this.dataset.textError);
        }
      }

      restoreForm(message) {
        this.form.classList.remove("product-notify-me__form--done");
        this.showStatus(message, true);
      }

      // Fire-and-forget: Shopify's native customer form records marketing consent
      // (honouring the store's double opt-in setting). Without it the restock
      // email — sent by Shopify Email — would be skipped for new addresses.
      subscribeMarketing(email) {
        const body = new URLSearchParams();
        body.append("form_type", "customer");
        body.append("utf8", "✓");
        body.append("contact[email]", email);
        body.append("contact[tags]", "back-in-stock");
        const root = window.Shopify?.routes?.root || "/";
        fetch(`${root}contact`, {
          method: "POST",
          body,
          headers: {
            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
            Accept: "text/html",
          },
        }).catch(() => {});
      }

      showStatus(message, isError) {
        if (!this.statusEl) return;
        this.statusEl.textContent = message || "";
        this.statusEl.classList.toggle("product-notify-me__status--error", Boolean(isError));
        this.statusEl.hidden = false;
      }

      hideStatus() {
        if (!this.statusEl) return;
        this.statusEl.textContent = "";
        this.statusEl.hidden = true;
      }
    }
  );
}
